/**
 * Client HTTP de l'API de sauvegarde.
 *
 * Il ne fait qu'un aller-retour et traduit les réponses : toute la logique de
 * file d'attente et de reprise vit dans l'adaptateur synchronisé.
 */
export interface SauvegardeDistante {
  readonly revision: number;
  readonly etat: string | null;
  /** Chapitres ouverts par l'enseignant. Absent quand l'API est injoignable. */
  readonly chapitres?: readonly string[];
}

export type ResultatEnvoi =
  | { readonly ok: true; readonly revision: number }
  | {
      readonly ok: false;
      readonly raison: 'conflit';
      readonly revision: number;
      readonly etat: string | null;
    }
  | { readonly ok: false; readonly raison: 'reseau' }
  | { readonly ok: false; readonly raison: 'refus'; readonly code: number };

/**
 * Ce que le serveur sait d'un code d'îlot.
 *
 * La distinction compte : « ce code n'existe pas » est une erreur de saisie à
 * signaler à l'élève, « je n'ai pas pu joindre le serveur » ne doit pas
 * l'empêcher de jouer.
 */
export type EtatDeLIlot = 'connu' | 'inconnu' | 'injoignable';

export interface ClientApi {
  charger(islandCode: string): Promise<SauvegardeDistante | null>;
  /** Le serveur connaît-il ce code d'îlot ? */
  verifierIlot(islandCode: string): Promise<EtatDeLIlot>;
  enregistrer(islandCode: string, revision: number, etat: string): Promise<ResultatEnvoi>;
}

/** Même origine par défaut : en production, l'API sert aussi le front. */
export function createClientApi(base = ''): ClientApi {
  const url = (chemin: string): string => `${base}${chemin}`;

  return {
    async verifierIlot(islandCode) {
      try {
        const reponse = await fetch(url(`/api/saves/${islandCode}`));
        if (reponse.ok) {
          return 'connu';
        }
        // 404 : l'îlot n'appartient à aucune classe. 400 : le format est mauvais,
        // ce que l'interface a déjà refusé — dans les deux cas, le code est faux.
        if (reponse.status === 404 || reponse.status === 400) {
          return 'inconnu';
        }
        // Le serveur répond mais va mal : on ne bloque pas une classe pour ça.
        return 'injoignable';
      } catch {
        return 'injoignable';
      }
    },

    async charger(islandCode) {
      try {
        const reponse = await fetch(url(`/api/saves/${islandCode}`));
        if (!reponse.ok) {
          return null;
        }
        const corps = (await reponse.json()) as SauvegardeDistante;
        return corps;
      } catch {
        // Réseau coupé ou API absente : l'appelant retombera sur le local.
        return null;
      }
    },

    async enregistrer(islandCode, revision, etat) {
      let reponse: Response;
      try {
        reponse = await fetch(url(`/api/saves/${islandCode}`), {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ revision, etat }),
        });
      } catch {
        return { ok: false, raison: 'reseau' };
      }

      if (reponse.status === 409) {
        const corps = (await reponse.json()) as { revision: number; etat: string | null };
        return { ok: false, raison: 'conflit', revision: corps.revision, etat: corps.etat };
      }

      if (!reponse.ok) {
        return { ok: false, raison: 'refus', code: reponse.status };
      }

      const corps = (await reponse.json()) as { revision: number };
      return { ok: true, revision: corps.revision };
    },
  };
}
